import React from 'react';

/**
 * Renderiza el bloque EvolutionChart de SCAPP V2.
 *
 * @param {object} props
 * @param {object} props.v - Modelo de presentación y callbacks del panel.
 * @returns {React.ReactNode} Bloque visual sin estado local.
 */
export default function EvolutionChart({
  v
}) {
  const axisRef = React.useRef(null);
  const measureRef = React.useRef(v.sMeasureX);
  measureRef.current = v.sMeasureX;
  React.useEffect(() => {
    const axis = axisRef.current;
    if (!axis) return;
    const measure = () => measureRef.current?.(
      axis.getBoundingClientRect().width,
      parseFloat(window.getComputedStyle(axis).fontSize)
    );
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(axis);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [v.tabEvo]);

  return v.tabEvo ? React.createElement(React.Fragment, null, React.createElement("div", {
    style: {
      "display": "grid",
      "gridTemplateColumns": v.sGridCols,
      "gap": "0.740741vh",
      "flex": "1",
      "minHeight": "12.037037vh"
    }
  }, v.text("\n          "), React.createElement("div", {
    style: {
      "position": "relative",
      "display": "grid",
      "gridTemplateColumns": "repeat(" + Math.max(1, (v.sYAxesLeft || []).length) + ",minmax(0,1fr))",
      "gap": "0.37037vh"
    }
  }, (v.sYAxesLeft || []).map((axis, index172) => React.createElement("div", {
    key: index172,
    title: axis.name,
    style: {
      "position": "relative",
      "minWidth": "0",
      "borderRight": "0.052083vw solid " + axis.c
    }
  }, axis.short ? React.createElement("span", {
    style: {
      "position": "absolute",
      "top": "50%",
      "left": "0.104167vw",
      "writingMode": "vertical-rl",
      "transform": "translateY(-50%) rotate(180deg)",
      "whiteSpace": "nowrap",
      "font": "600 0.833333vh 'IBM Plex Mono',monospace",
      "color": axis.c,
      "textAlign": "center"
    }
  }, v.text(axis.short)) : null, (axis.ticks || []).map((t, index173) => React.createElement("span", {
    key: index173,
    style: {
      "position": "absolute",
      "right": "0.15625vw",
      "bottom": t.pos,
      "transform": "translateY(50%)",
      "font": "0.925926vh 'IBM Plex Mono',monospace",
      "color": axis.c
    }
  }, v.text(t.lab))))), v.text("\n          ")), v.text("\n          "), React.createElement("div", {
    "onMouseMove": v.sMoveZ,
    "onMouseLeave": v.sLeaveZ,
    "onMouseDown": v.sDownZ,
    "onMouseUp": v.sUpZ,
    "onWheel": v.sWheelZ,
    "data-wheel-zoom": "1",
    "onClick": v.sClickZ,
    style: {
      "position": "relative",
      "borderBottom": "0.092593vh solid #2a3446",
      "cursor": v.sCursor,
      "userSelect": "none"
    }
  }, v.text("\n            "), React.createElement("div", {
    style: {
      "position": "absolute",
      "top": "0",
      "bottom": "0",
      "left": v.sSelL,
      "width": v.sSelW,
      "background": "rgba(228,233,240,0.12)",
      "borderLeft": "0.052083vw solid #8793a6",
      "borderRight": "0.052083vw solid #8793a6",
      "pointerEvents": "none",
      "display": v.sSelD,
      "zIndex": "2"
    }
  }, null), v.text("\n            "), (v.sYTicks || []).map((t, index60) => React.createElement(React.Fragment, {
    key: index60
  }, React.createElement("div", {
    style: {
      "position": "absolute",
      "left": "0",
      "right": "0",
      "bottom": t.pos,
      "borderTop": "0.092593vh solid #151e2c"
    }
  }, null))), v.text("\n            "), React.createElement("svg", {
    "viewBox": v.sVB,
    "preserveAspectRatio": "none",
    style: {
      "position": "absolute",
      "inset": "0",
      "width": "100%",
      "height": "100%",
      "display": "block",
      "overflow": "visible"
    }
  }, v.text("\n              "), (v.sBars || []).map((b, index61) => React.createElement(React.Fragment, {
    key: index61
  }, React.createElement("path", {
    "d": b.d,
    "fill": b.c
  }, null))), v.text("\n              "), (v.sLines || []).map((l, index62) => React.createElement(React.Fragment, {
    key: index62
  }, React.createElement("path", {
    "d": l.d,
    "stroke": l.c,
    "strokeDasharray": l.dash,
    "strokeWidth": "2.2",
    "fill": "none",
    "vectorEffect": "non-scaling-stroke",
    "strokeLinejoin": "round"
  }, null))), v.text("\n              "), React.createElement("path", {
    "d": v.sRef,
    "stroke": "#e4e9f0",
    "strokeWidth": "2",
    "fill": "none",
    "vectorEffect": "non-scaling-stroke",
    "strokeDasharray": "6 4"
  }, null), v.text("\n            ")), (v.sMarks || []).map((m, index63) => React.createElement(React.Fragment, {
    key: index63
  }, React.createElement("div", {
    style: {
      "position": "absolute",
      "bottom": "0",
      "left": m.l,
      "width": m.w,
      "height": m.h,
      "background": m.bg,
      "boxShadow": "inset 0 0 0 0.092593vh " + m.c,
      "pointerEvents": "none"
    }
  }, null))), v.text("\n            "), React.createElement("div", {
    style: {
      "position": "absolute",
      "top": "0",
      "bottom": "0",
      "left": v.sX,
      "width": "0.052083vw",
      "background": "#e4e9f0",
      "opacity": "0.6",
      "pointerEvents": "none",
      "display": v.sTipDisp
    }
  }, null), v.text("\n            "), React.createElement("div", {
    "onMouseMove": v.tipStop,
    "onMouseDown": v.tipStop,
    "onClick": v.tipStop,
    "onWheel": v.tipStop,
    "data-tip-scroll": "1",
    style: {
      "position": "absolute",
      "top": "0.555556vh",
      "left": v.sX,
      "transform": v.sTipTx,
      "minWidth": "9.895833vw",
      "width": v.sTipW,
      "maxWidth": "min(39.583333vw, calc(100vw - 2.5vw))",
      "background": "rgba(4,10,20,0.96)",
      "border": "0.092593vh solid #2a3446",
      "borderRadius": "0.740741vh",
      "padding": "0.740741vh 1.111111vh",
      "display": v.sTipDisp,
      "flexDirection": "column",
      "gap": "0.37037vh",
      "pointerEvents": "auto",
      "cursor": "default",
      "maxHeight": "max(14.814815vh, calc(100% - 1.111111vh))",
      "overflowX": "auto",
      "overflowY": "auto",
      "overscrollBehavior": "contain",
      "scrollbarWidth": "thin",
      "zIndex": "4"
    }
  }, v.text("\n              "), React.createElement("div", {
    style: {
      "position": "sticky",
      "top": "-0.740741vh",
      "background": "rgba(4,10,20,0.98)",
      "padding": "0.185185vh 0 0.37037vh",
      "marginTop": "-0.185185vh",
      "zIndex": "1",
      "font": "500 1.203704vh 'IBM Plex Mono',monospace"
    }
  }, v.text(v.sTipDate)), v.text("\n              "), (v.sTipColumns || []).length ? React.createElement("div", {
    style: {
      "display": "grid",
      "gridTemplateColumns": "repeat(" + (v.sTipColumns || []).length + ",minmax(7.291667vw,1fr))",
      "gap": "0.925926vh",
      "alignItems": "start"
    }
  }, (v.sTipColumns || []).map((column, index174) => React.createElement("div", {
    key: index174,
    style: {
      "minWidth": "7.291667vw",
      "paddingLeft": index174 ? "0.520833vw" : "0",
      "borderLeft": index174 ? "0.052083vw solid rgba(46,179,238,0.14)" : "none"
    }
  }, React.createElement("div", {
    style: {
      "display": "flex",
      "alignItems": "center",
      "gap": "0.555556vh",
      "paddingBottom": "0.37037vh",
      "marginBottom": "0.37037vh",
      "borderBottom": "0.092593vh solid rgba(46,179,238,0.14)",
      "fontWeight": "600",
      "color": column.c
    }
  }, React.createElement("span", {
    style: {
      "width": "0.729167vw",
      "height": "0.925926vh",
      "borderRadius": "0.185185vh",
      "background": column.bg,
      "boxShadow": "inset 0 0 0 0.092593vh " + column.c,
      "flex": "none"
    }
  }, null), React.createElement("span", {
    style: {
      "whiteSpace": "nowrap"
    }
  }, v.text(column.name)), React.createElement("span", {
    style: {
      "marginLeft": "auto",
      "font": "0.925926vh 'IBM Plex Mono',monospace",
      "color": "#5a667a",
      "whiteSpace": "nowrap"
    }
  }, v.text(column.range))), (column.rows || []).map((r, index175) => React.createElement("div", {
    key: index175,
    style: {
      "display": "flex",
      "justifyContent": "space-between",
      "alignItems": "center",
      "gap": "0.925926vh",
      "padding": "0.185185vh 0",
      "fontSize": "1.111111vh"
    }
  }, React.createElement("span", {
    style: {
      "display": "flex",
      "alignItems": "center",
      "gap": "0.555556vh",
      "color": r.nc,
      "fontWeight": r.fw,
      "whiteSpace": "nowrap"
    }
  }, React.createElement("span", {
    style: {
      "width": r.sw,
      "height": r.sh,
      "borderRadius": "0.185185vh",
      "background": r.c,
      "flex": "none"
    }
  }, null), v.text(r.name)), React.createElement("span", {
    style: {
      "fontFamily": "'IBM Plex Mono',monospace",
      "color": "#e4e9f0",
      "whiteSpace": "nowrap"
    }
  }, v.text(r.val))))))) : null, v.text("\n              "), (v.sTipRows || []).map((r, index64) => React.createElement(React.Fragment, {
    key: index64
  }, React.createElement("div", {
    style: {
      "display": "flex",
      "justifyContent": "space-between",
      "alignItems": "center",
      "gap": "1.296296vh",
      "fontSize": "1.111111vh",
      "marginTop": r.mt,
      "paddingLeft": r.pl,
      "borderTop": r.bt,
      "paddingTop": r.pt
    }
  }, React.createElement("span", {
    style: {
      "display": "flex",
      "alignItems": "center",
      "gap": "0.555556vh",
      "color": r.nc,
      "fontWeight": r.fw
    }
  }, React.createElement("span", {
    style: {
      "width": r.sw,
      "height": r.sh,
      "borderRadius": "0.185185vh",
      "background": r.c,
      "boxShadow": r.ring,
      "flex": "none"
    }
  }, null), v.text(r.name)), React.createElement("span", {
    style: {
      "fontFamily": "'IBM Plex Mono',monospace",
      "color": "#e4e9f0"
    }
  }, v.text(r.val))))), v.text("\n              "), React.createElement("div", {
    style: {
      "fontSize": "1.018519vh",
      "color": "#5a667a",
      "borderTop": "0.092593vh solid rgba(46,179,238,0.12)",
      "paddingTop": "0.37037vh"
    }
  }, v.text(v.sTipFoot)), v.text("\n            ")), v.text("\n          ")), v.text("\n          "), React.createElement("div", {
    style: {
      "position": "relative",
      "display": "grid",
      "gridTemplateColumns": "repeat(" + Math.max(1, (v.sYAxesRight || []).length) + ",minmax(0,1fr))",
      "gap": "0.37037vh"
    }
  }, (v.sYAxesRight || []).map((axis, index174) => React.createElement("div", {
    key: index174,
    title: axis.name,
    style: {
      "position": "relative",
      "minWidth": "0",
      "borderLeft": "0.052083vw solid " + axis.c
    }
  }, axis.short ? React.createElement("span", {
    style: {
      "position": "absolute",
      "top": "50%",
      "right": "0.104167vw",
      "writingMode": "vertical-rl",
      "transform": "translateY(-50%)",
      "whiteSpace": "nowrap",
      "font": "600 0.833333vh 'IBM Plex Mono',monospace",
      "color": axis.c,
      "textAlign": "center"
    }
  }, v.text(axis.short)) : null, (axis.ticks || []).map((t, index175) => React.createElement("span", {
    key: index175,
    style: {
      "position": "absolute",
      "left": "0.15625vw",
      "bottom": t.pos,
      "transform": "translateY(50%)",
      "font": "0.925926vh 'IBM Plex Mono',monospace",
      "color": axis.c
    }
  }, v.text(t.lab))))), v.text("\n            "), (v.sEnd || []).map((e, index65) => React.createElement(React.Fragment, {
    key: index65
  }, React.createElement("span", {
    style: {
      "position": "absolute",
      "left": "0",
      "top": e.top,
      "transform": "translateY(-50%)",
      "font": "1.018519vh 'IBM Plex Mono',monospace",
      "fontWeight": "500",
      "color": e.c,
      "whiteSpace": "nowrap"
    }
  }, v.text(e.lab)))), v.text("\n          ")), v.text("\n        ")), v.text("\n        "), React.createElement("div", {
    style: {
      "display": "grid",
      "gridTemplateColumns": v.sGridCols,
      "gap": "0.740741vh",
      "font": "1.018519vh 'IBM Plex Mono',monospace",
      "color": "#5a667a"
    }
  }, v.text("\n          "), React.createElement("div", {}, null), v.text("\n          "), React.createElement("div", {
    ref: axisRef,
    "data-evolution-x-axis": "1",
    style: {
      "position": "relative",
      "height": "1.296296vh"
    }
  }, (v.sXTicks || []).map((t, index66) => React.createElement(React.Fragment, {
    key: index66
  }, React.createElement("span", {
    style: {
      "position": "absolute",
      "left": t.pos,
      "transform": t.tx || "translateX(-50%)",
      "whiteSpace": "nowrap"
    }
  }, v.text(t.lab))))), v.text("\n          "), React.createElement("div", {}, null), v.text("\n        ")), v.text("\n        "), React.createElement("div", {
    style: {
      "flexWrap": "nowrap",
      "overflowX": "auto",
      "overflowY": "hidden",
      "whiteSpace": "nowrap",
      "scrollbarWidth": "thin",
      "flex": "none",
      "display": "flex",
      "gap": "1.481481vh",
      "alignItems": "center",
      "fontSize": "1.111111vh",
      "color": "#8793a6",
      "paddingLeft": "2.5vw"
    }
  }, v.text("\n          "), (v.sMixLeg || []).map((l, index67) => React.createElement(React.Fragment, {
    key: index67
  }, React.createElement("span", {
    style: {
      "display": "flex",
      "alignItems": "center",
      "gap": "0.555556vh",
      "color": "#e4e9f0"
    }
  }, React.createElement("span", {
    style: {
      "width": "0.833333vw",
      "height": "1.296296vh",
      "borderRadius": "0.185185vh",
      "background": l.bg,
      "boxShadow": "inset 0 0 0 0.092593vh #b8c1ce"
    }
  }, null), v.text(l.name)))), v.text("\n          "), (v.sLegend8 || []).map((l, index68) => React.createElement(React.Fragment, {
    key: index68
  }, React.createElement("span", {
    style: {
      "whiteSpace": "nowrap",
      "flex": "none",
      "display": "flex",
      "alignItems": "center",
      "gap": "0.555556vh",
      "color": "#e4e9f0"
    }
  }, React.createElement("span", {
    style: {
      "width": "0.520833vw",
      "height": "0.925926vh",
      "borderRadius": "0.185185vh",
      "background": l.c
    }
  }, null), v.text(l.name)))), v.text("\n          \n        "))) : null;
}
